<template>
  <div class="container mt-4">
    <b-card title="Panel de Control de Componentes" class="shadow">
      <!-- Selector de Componente -->
      <b-form-group label="Seleccionar Componente:" label-for="component-select">
        <b-form-select 
          id="component-select"
          v-model="selectedComponent"
          :options="componentOptions"
          @change="loadComponentControls"
          class="mb-4"
        >
          <template #first>
            <b-form-select-option :value="null" disabled>
              -- Seleccione un componente --
            </b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>

      <b-form-group label="Seleccionar Rol:" label-for="role-select">
        <b-form-select
          id="role-select"
          v-model="selectedRole"
          :options="roleOptions"
          @change="loadComponentControls"
          class="mb-4"
          :disabled="loadingRoles"
        >
          <template #first>
            <b-form-select-option :value="null" disabled>
              -- Seleccione un rol --
            </b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>

      <!-- Controles del Componente Seleccionado -->
      <div v-if="selectedComponent && selectedRole">
        <h5 class="mb-3">Controles para: {{ selectedComponent }} | Rol: {{ selectedRole }}</h5>
        <div class="mb-3 text-muted" v-if="loadingControls">
          <small>
            <b-icon-arrow-repeat animation="spin" class="mr-1"></b-icon-arrow-repeat>
            Cargando controles...
          </small>
        </div>
        
        <b-list-group>
          <b-list-group-item 
            v-for="(isVisible, controlName) in componentControls" 
            :key="controlName"
            class="d-flex justify-content-between align-items-center"
          >
            <span>{{ getControlLabel(controlName) }}</span>
            <b-form-checkbox 
              v-model="componentControls[controlName]"
              switch
              @change="updateControl(controlName, $event)"
            >
              {{ componentControls[controlName] ? 'Visible' : 'Oculto' }}
            </b-form-checkbox>
          </b-list-group-item>
        </b-list-group>

        <!-- Botón para guardar todos los cambios -->
        <div class="mt-4">
          <b-button 
            variant="primary" 
            @click="saveAllControls"
            :disabled="!hasChanges"
          >
            <b-icon-save class="mr-2"></b-icon-save>
            Guardar Cambios
          </b-button>
          
          <b-button 
            variant="outline-secondary" 
            @click="resetChanges"
            class="ml-2"
            :disabled="!hasChanges"
          >
            Cancelar
          </b-button>
        </div>
      </div>

      <!-- Mensaje cuando no hay componente seleccionado -->
      <div v-else class="text-center text-muted py-4">
        <b-icon-gear font-scale="3" class="mb-3"></b-icon-gear>
        <p>Seleccione un componente y un rol para configurar sus controles</p>
      </div>
    </b-card>
  </div>
</template>

<script>
import BotonesControl from "../../services/BotonesControl"
import Roles from "../../services/Roles"

const CONTROL_CONFIG = {
  BalanceNiif: {
    scope: "BALNIIF",
    label: "Balance NIIF",
    controls: [
      { key: "Balance", id_boton: 100, label: "Checkbox Balance" },
      { key: "Comparativo", id_boton: 200, label: "Checkbox Comparativo" },
      { key: "Consolidado", id_boton: 300, label: "Checkbox Consolidado" },
      { key: "Notas", id_boton: 400, label: "Checkbox Notas" },
      { key: "Informacion", id_boton: 500, label: "Checkbox Información" },
      { key: "SelectorMoneda", id_boton: 600, label: "Mostrar selector de moneda" },
      { key: "MonedaFuncional1", id_boton: 610, label: "Permitir moneda funcional 1" },
      { key: "MonedaFuncional2", id_boton: 620, label: "Permitir moneda funcional 2" }
    ]
  }
}

export default {
  name: 'ButtonAdminPanel',
  
  data() {
    return {
      selectedComponent: null,
      selectedRole: null,
      roles: [],
      componentControls: {},
      originalControls: {},
      hasChanges: false,
      loadingControls: false,
      loadingRoles: false
    }
  },
  
  computed: {
    componentOptions() {
      return Object.keys(CONTROL_CONFIG).map((componentName) => ({
        value: componentName,
        text: CONTROL_CONFIG[componentName].label
      }))
    },
    roleOptions() {
      return this.roles.map((rol) => ({
        value: Number(rol.id),
        text: `${rol.nombre} (${rol.id})`
      }))
    },
    canLoadControls() {
      return !!this.selectedComponent && !!this.selectedRole
    }
  },
  
  methods: {
    async loadRoles() {
      this.loadingRoles = true
      try {
        const response = await Roles.getAllRoles()
        const roles = Array.isArray(response.data.roles) ? response.data.roles : []
        this.roles = roles

        const currentRole = Number(this.$store.state.userRole)
        if (Number.isInteger(currentRole) && currentRole > 0) {
          this.selectedRole = currentRole
        } else if (roles.length > 0) {
          this.selectedRole = Number(roles[0].id)
        }
      } catch (error) {
        console.error("Error cargando roles:", error)
        this.$bvToast.toast('No fue posible cargar roles', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loadingRoles = false
      }
    },

    getComponentDefinition() {
      return CONTROL_CONFIG[this.selectedComponent] || null
    },

    getDefaultControls(componentName) {
      const definition = CONTROL_CONFIG[componentName]
      if (!definition) return {}

      return definition.controls.reduce((acc, item) => {
        acc[item.key] = true
        return acc
      }, {})
    },

    getControlLabel(controlName) {
      const definition = this.getComponentDefinition()
      if (!definition) return controlName

      const found = definition.controls.find((item) => item.key === controlName)
      return found ? found.label : controlName
    },
    
    async loadComponentControls() {
      if (!this.canLoadControls) return

      const definition = this.getComponentDefinition()
      const defaults = this.getDefaultControls(this.selectedComponent)
      const currentControls = this.$store.getters.getComponentControls(this.selectedComponent)
      const baseControls = { ...defaults, ...currentControls }

      this.loadingControls = true
      try {
        const response = await BotonesControl.getBotonesControl(definition.scope, this.selectedRole)
        const botones = Array.isArray(response.data.botones) ? response.data.botones : []
        const nextControls = { ...baseControls }

        definition.controls.forEach((item) => {
          const fromDb = botones.find((boton) => Number(boton.id_boton) === item.id_boton)
          if (fromDb) {
            nextControls[item.key] = fromDb.enabled !== false
          }
        })

        this.$store.commit('setComponentControls', {
          componentName: this.selectedComponent,
          controls: { ...nextControls }
        })

        this.componentControls = { ...nextControls }
        this.originalControls = { ...nextControls }
        this.hasChanges = false
      } catch (error) {
        console.error('Error cargando controles:', error)
        this.componentControls = { ...baseControls }
        this.originalControls = { ...baseControls }
        this.hasChanges = false

        this.$bvToast.toast('No fue posible cargar controles desde la base de datos. Se usan valores locales.', {
          title: 'Advertencia',
          variant: 'warning',
          solid: true
        })
      } finally {
        this.loadingControls = false
      }
    },
    
    updateControl() {

      this.hasChanges = true
    },
    
    async saveAllControls() {
      if (!this.canLoadControls) return

      const definition = this.getComponentDefinition()
      if (!definition) return

      const botones = definition.controls.map((control) => ({
        id_boton: control.id_boton,
        enabled: this.componentControls[control.key] === true
      }))

      try {
        await BotonesControl.saveBotonesControl(
          definition.scope,
          botones,
          this.$store.state.userId,
          this.selectedRole
        )

        this.$store.commit('setComponentControls', {
          componentName: this.selectedComponent,
          controls: { ...this.componentControls }
        })
        
        this.originalControls = { ...this.componentControls }
        this.hasChanges = false
        
        this.$bvToast.toast('Controles guardados exitosamente', {
          title: 'Éxito',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        console.error('Error guardando controles:', error)
        this.$bvToast.toast('Error al guardar controles en base de datos', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      }
    },
    
    resetChanges() {
      this.componentControls = { ...this.originalControls }
      this.hasChanges = false
    }
  },

  mounted() {
    this.loadRoles()
  },
  
  watch: {
    selectedRole() {
      this.loadComponentControls()
    },
    componentControls: {
      deep: true,
      handler(newVal) {
        const changes = Object.keys(newVal).some(key => 
          newVal[key] !== this.originalControls[key]
        )
        this.hasChanges = changes
      }
    }
  }
}
</script>

<style scoped>
.list-group-item {
  transition: all 0.3s ease;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}
</style>
